import { motion } from 'motion/react';

import shieldIllustration from 'assets/images/wallet-onboarding-shield.png';
import { Button } from 'components/atoms/Button';
import { Modal } from 'components/atoms/Modal';
import GivingCoinsIcon from 'components/atoms/SVG/GivingCoinsIcon.component';
import ModalHeading from 'components/molecules/ModalHeading';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { fadeTransition } from 'utils/constants';
import formatMoney from 'utils/formatMoney';

const ZeroHiddenFees: FC<{ onConfirm?: () => void }> = ({ onConfirm }) => {
  const { t } = useTranslation();

  return (
    <motion.div
      {...fadeTransition}
      className="flex items-center justify-center flex-col relative pb-32"
      key="3"
    >
      <ModalHeading
        Icon={GivingCoinsIcon}
        title={t('wallet.tutorial.slide_b.title.new')}
        subtitle={t('wallet.tutorial.slide_b.fee_list.subtitle')}
        titleClassName="w-[80%]"
        subtitleClassName="w-[80%]"
      />

      <div className="flex items-start mt-10">
        <img src={shieldIllustration} className="w-44" />
      </div>

      <Button
        color="bank"
        className="mt-6 w-[80%] absolute bottom-10 capitalize"
        onClick={onConfirm}
      >
        {t('next')}
      </Button>

      <Modal
        name="Accountable Banking Onboarding - Fees list modal"
        trigger={(modalApi) => (
          <Button
            color="bank"
            structure="text"
            className="mt-6 w-[80%] absolute bottom-0 capitalize"
            onClick={() => modalApi.openModal()}
          >
            {t('wallet.tutorial.slide_b.cta')}
          </Button>
        )}
        className="!max-w-[40rem]"
        render={(api) => {
          return (
            <motion.div
              {...fadeTransition}
              className="flex items-center justify-center flex-col relative"
            >
              <ModalHeading Icon={GivingCoinsIcon} title="Zero hidden fees" />

              <div className="my-4 text-center border border-taxes-100 rounded-lg w-[80%]">
                <div className="flex items-center justify-between p-5 py-3 border-b border-taxes-100">
                  <div className="flex flex-col items-start">
                    <h3 className="text-primary-700 capitalize text-sm">
                      {t('wallet.tutorial.slide_b.fee_list.a')}
                    </h3>

                    <p className="text-primary font-semibold text-sm">€0</p>
                  </div>
                </div>

                <div className="flex items-center justify-between p-5 py-3 border-b border-taxes-100">
                  <div className="flex flex-col items-start">
                    <h3 className="text-primary-700 capitalize text-sm">
                      {t('wallet.tutorial.slide_b.fee_list.b')}
                    </h3>

                    <p className="text-primary font-semibold text-sm">€0</p>
                  </div>
                </div>

                <div className="flex items-center justify-between p-5 py-3 border-b border-taxes-100">
                  <div className="flex flex-col items-start">
                    <h3 className="text-primary-700 text-sm">
                      {t('wallet.tutorial.slide_b.fee_list.c')}
                    </h3>

                    <p className="text-primary font-semibold text-sm">€0</p>
                  </div>
                </div>

                <div className="flex items-center justify-between p-5 py-3 border-b border-taxes-100">
                  <div className="flex flex-col items-start">
                    <h3 className="text-primary-700 text-sm">
                      {t('wallet.tutorial.slide_b.fee_list.d')}
                    </h3>

                    <p className="text-primary font-semibold text-sm">€0</p>
                  </div>
                </div>

                <div className="flex items-center justify-between p-5 py-3 border-b border-taxes-100">
                  <div className="flex flex-col items-start">
                    <h3 className="text-primary-700 text-sm">
                      {t('wallet.tutorial.slide_b.fee_list.e')}
                    </h3>

                    <p className="text-primary font-semibold first-letter:capitalize text-sm">
                      €0
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-between p-5 py-3 border-b border-taxes-100">
                  <div className="flex flex-col items-start">
                    <h3 className="text-primary-700 text-sm">
                      {t('wallet.tutorial.slide_b.fee_list.f')}
                    </h3>

                    <p className="text-primary font-semibold first-letter:capitalize text-sm">
                      €0
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-between p-5 py-3 border-b border-taxes-100">
                  <div className="flex flex-col items-start">
                    <h3 className="text-primary-700 text-sm">
                      {t('wallet.tutorial.slide_b.fee_list.g')}
                    </h3>

                    <p className="text-primary font-semibold first-letter:capitalize text-sm">
                      €0
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-between p-5 py-3 border-b border-taxes-100">
                  <div className="flex flex-col items-start">
                    <h3 className="text-primary-700 text-sm">
                      {t('wallet.tutorial.slide_b.fee_list.h')}
                    </h3>

                    <p className="text-primary font-semibold first-letter:capitalize text-sm">
                      {t('wallet.tutorial.slide_b.fee_list.h.fee_amount', {
                        currencySymbol: '€',
                      })}
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-between p-5 py-3 border-b border-taxes-100">
                  <div className="flex flex-col items-start">
                    <h3 className="text-primary-700 text-sm">
                      {t('wallet.tutorial.slide_b.fee_list.i')}
                    </h3>

                    <p className="text-primary font-semibold first-letter:capitalize text-sm">
                      {t('wallet.tutorial.slide_b.fee_list.i.value', {
                        amount: formatMoney(5),
                      })}
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-between p-5 py-3">
                  <div className="flex flex-col items-start">
                    <h3 className="text-primary-700 text-sm">
                      {t('wallet.tutorial.slide_b.fee_list.j')}
                    </h3>

                    <p className="text-primary font-semibold first-letter:capitalize text-sm">
                      {t('wallet.tutorial.slide_b.fee_list.j.value')}
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-between p-5 py-3">
                  <div className="flex flex-col items-start">
                    <h3 className="text-primary-700 text-sm">
                      {t('banking_onboarding.fees.card_payments_outside_eu')}
                    </h3>

                    <p className="text-primary font-semibold first-letter:capitalize text-sm">
                      {t(
                        'banking_onboarding.fees.card_payments_outside_eu.percent',
                      )}
                    </p>
                  </div>
                </div>
              </div>

              <Button
                color="bank"
                className="mt-6 w-[80%] "
                onClick={() => api.closeModal()}
              >
                {t('close')}
              </Button>
            </motion.div>
          );
        }}
      />
    </motion.div>
  );
};

export default ZeroHiddenFees;
